import React from "react";
import { LegalPageLayout } from "./LegalPageLayout";

const sections = [
  {
    title: "Data Collection and Usage",
    paragraphs: [
      "We collect and process only the minimum information we need to operate Echo MSG safely and reliably.",
    ],
    bullets: [
      "Your WhatsApp phone number or user ID and, where available, your WhatsApp profile name.",
      "Service usage metadata such as how many conversions or messages you have used, message timestamps, audio duration, text length, subscription status, and referral or bonus status.",
      "If you start a WhatsApp chat from a Meta ad, the ad ID, ad link, and Meta's click identifier included with your first message. We use these to measure ad signups, first tryouts, and purchases.",
      "If you contact support, the information you choose to share with us.",
      "If you purchase a subscription through PayFast, we may receive payment-related details such as payment status and, where provided, your name and email address. We do not store full card details.",
      "When you use our website, we may receive standard site analytics such as IP address, browser type, pages visited, and visit times.",
    ],
  },
  {
    title: "How Voice Notes, Images, and Transcriptions Are Handled",
    paragraphs: [
      "For voice-note transcription, your audio is processed in real time so we can return text to you on WhatsApp. The audio file may be downloaded temporarily for processing and is then deleted after the transcription job finishes.",
      "When you send an image for text recognition, we download it temporarily, resize it if needed, and send it to OCR.space so we can return the visible text to you on WhatsApp. We do not save the image in our own database.",
      "Echo MSG does not keep a stored archive of your voice notes, images, transcripts, extracted image text, or summaries in its own database after delivery. We also do not save full chat history for context-based replies.",
      "Some related features may require temporary file handling so we can deliver the result back to you. For example, if you use text-to-audio features, the generated file may be stored briefly until delivery and then deleted.",
    ],
  },
  {
    title: "Third-Party Services We Use",
    paragraphs: [
      "To provide the service, we rely on carefully chosen third-party providers that process data on our behalf.",
    ],
    bullets: [
      "WhatsApp and Meta, to receive your messages, send back results, measure outcomes from Click to WhatsApp ads, and create advertising audiences as described below.",
      "Google Cloud, to host the service, store account metadata, and support temporary processing.",
      "AssemblyAI, to transcribe voice notes into text.",
      "OCR.space, to extract visible text from images you send for conversion.",
      "OpenAI, when we generate summaries or call-to-action highlights for longer notes.",
      "PayFast, to process subscriptions and recurring billing.",
    ],
  },
  {
    title: "How We Use Your Information",
    bullets: [
      "To deliver transcriptions, summaries, and related service features.",
      "To track message usage, free trial limits, referral rewards, and subscription status.",
      "To understand which Meta ads led to a first tryout or a purchase. For ad-originated chats, we may send Meta the ad click identifier, the outcome type and time, and, for a purchase, its amount and currency. We do not send Meta the content of your voice notes, photos, or converted text for this measurement.",
      "To create Meta advertising audiences from people who have used Echo MSG, we may share your WhatsApp phone number with Meta in a hashed form. We select audience members using usage information such as whether you converted a message recently. This can help us reach existing users or people similar to them with ads. We do not share your message content or individual conversion history in the customer list.",
      "To process payments, prevent abuse or fraud, and support customer service.",
      "To send occasional service-related reminders or marketing messages about Echo MSG, where allowed, and to store your messaging preferences.",
      "To improve reliability, performance, and the overall user experience.",
      "To meet legal, accounting, or security obligations where required.",
    ],
  },
  {
    title: "Security",
    paragraphs: [
      "Messages sent in WhatsApp use WhatsApp's security controls while in transit. Once delivered to Echo MSG, we use reasonable technical and organizational safeguards to protect data during processing.",
      "No online service can promise absolute security, but we work to limit access and reduce the amount of personal data we keep.",
    ],
  },
  {
    title: "No Chat History",
    paragraphs: [
      "Unlike many chatbots, Echo MSG is designed not to keep a memory of your past message content. That is why the service may not be able to answer questions based on previous conversations.",
      "We have made that tradeoff deliberately to reduce the amount of private content we retain.",
    ],
  },
  {
    title: "Data Retention",
    paragraphs: [
      "We keep account, billing, referral, ad attribution, and usage metadata only for as long as needed to run the service, measure advertising, manage subscriptions, prevent abuse, resolve disputes, and meet legal or accounting obligations.",
      "Temporary content files are intended to be deleted promptly after processing and delivery.",
    ],
  },
  {
    title: "Your Choices",
    bullets: [
      "You can stop using Echo MSG at any time.",
      "You can opt out of Echo MSG marketing messages and use of your number in our Meta customer-list audiences at any time by replying STOP. We exclude opted-out users from new lists and remove them from lists we update.",
      "To cancel a paid subscription or request deletion of account metadata that we control, email echomsgteam@gmail.com.",
      "If your request relates to data handled directly by third-party services such as WhatsApp or PayFast, you may also need to contact those providers.",
    ],
  },
  {
    title: "Changes to This Privacy Policy",
    paragraphs: [
      "We may update this Privacy Policy from time to time. When we do, we will post the updated version on this page. Continued use of the service after an update means you accept the revised policy.",
    ],
  },
  {
    title: "Contact Us",
    paragraphs: [
      "If you have questions about this Privacy Policy or how your data is handled, please contact us at echomsgteam@gmail.com.",
    ],
  },
];

const PrivacyPolicy = () => {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      lastUpdated="October 4, 2026"
      introTitle="Our Commitment to Your Privacy"
      introParagraphs={[
        "At Echo MSG, we try to keep message-content storage to a minimum. We need some account and usage data to run the service, but we do not keep a stored archive of your voice notes, images, transcripts, extracted image text, or summaries in our own database after delivery.",
        "This Privacy Policy explains, in plain English, what we collect, how we use it, and how we work to protect it when you use Echo MSG for WhatsApp.",
      ]}
      sections={sections}
    />
  );
};

export default PrivacyPolicy;
